import IDataComponent, { Selectable, Stream } from 'src/models/IDataComponent'

/*
 * The streams a pipeline's sources were last found to have (MEL-702). The catalog writes them out in
 * each format, at the pipeline's `schema` link (PipelineSchemaExport), so the Streams tab, the CLI and
 * a workspace's dbt sources all say the same: this only names the formats and the files they save as.
 */

/** A source and the streams its last discovery found. */
export interface ISourceSchema {
    name: string
    streams: Stream[]
}

/** As the `schema` link's `format` parameter names them. */
export enum SchemaFormat {
    Markdown = 'markdown',
    Json = 'json',
    SingerCatalog = 'singer-catalog',
    DbtSources = 'dbt-sources',
}

export const schemaFormats: { format: SchemaFormat, label: string }[] = [
    { format: SchemaFormat.Markdown, label: 'Markdown' },
    { format: SchemaFormat.Json, label: 'JSON' },
    { format: SchemaFormat.SingerCatalog, label: 'Singer Catalog' },
    { format: SchemaFormat.DbtSources, label: 'DBT Sources' },
]

/** The pipeline's sources with discovered streams: a source with none hasn't been discovered. */
export const sourceSchemas = (components: IDataComponent[]): ISourceSchema[] => components
    .filter(dc => !!dc.streams?.length)
    .map(dc => ({ name: dc.name, streams: dc.streams! }))

// AUTOMATIC is a stream or field the source always sends, so it is selected too (ExtractorStreamSelection)
export const isSelected = (s: Selectable): boolean => s.selected !== 'EXCLUDED'

export const selectionLabel = (s: Selectable): string =>
    s.selected === 'AUTOMATIC' ? 'Automatic' : s.selected === 'SELECTED' ? 'Selected' : 'Excluded'

/** The `schema` link in a format: the link the catalog handed us, so merged into any query it has. */
export const schemaFormatURI = (href: string, format: SchemaFormat): string =>
    `${href}${href.includes('?') ? '&' : '?'}format=${encodeURIComponent(format)}`

const fileSuffix: Record<SchemaFormat, string> = {
    [SchemaFormat.Markdown]: '-schema.md',
    [SchemaFormat.Json]: '-schema.json',
    [SchemaFormat.SingerCatalog]: '-catalog.json',
    [SchemaFormat.DbtSources]: '-sources.yml',
}

// as the catalog's Content-Disposition names it, which a cross-origin response doesn't let us read
export const schemaFileName = (pipelineName: string, format: SchemaFormat): string =>
    `${pipelineName.replace(/[^\w.-]+/g, '_')}${fileSuffix[format]}`
